import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Modal, TableBody } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import CreateBankingAcc from "../../components/BankingAccount/CreateBankingAcc";
import { ALL_BANKING_ACC } from "../../gql/bankingAcc";
import {
  LoadingModal,
  DataTable,
  DataTableCell,
  DataTablePagination,
  DataTableRow,
  ListPageLayout,
  RowActions,
} from "../../components/layout";

const COLUMNS = [
  { id: "id", label: "ID", minWidth: 60 },
  { id: "image", label: "Image", minWidth: 80 },
  { id: "service", label: "Payment Service Name", minWidth: 160 },
  { id: "receiver", label: "Receiver Name", minWidth: 140 },
  { id: "account", label: "Account Number", minWidth: 140 },
  { id: "info", label: "Information", minWidth: 140 },
  { id: "created", label: "Created At", minWidth: 120 },
  { id: "actions", label: "Actions", minWidth: 100 },
];

const BankingAccs = ({ packageId }) => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [offset, setOffset] = useState(0);
  const [bankingAccounts, setBankingAccounts] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [loadBankingAccs, result] = useLazyQuery(ALL_BANKING_ACC);

  useEffect(() => {
    loadBankingAccs({
      variables: {
        limit: rowsPerPage,
        offset: offset,
      },
      fetchPolicy: "network-only",
    });
  }, [loadBankingAccs, offset, rowsPerPage]);

  useEffect(() => {
    if (result.data) {
      setBankingAccounts(result?.data?.banking_accounts);
      setCount(
        Number(result.data?.banking_accounts_aggregate?.aggregate.count)
      );
    }
  }, [result]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setOffset(rowsPerPage * newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    setOffset(0);
  };

  if (!bankingAccounts) {
    return <LoadingModal open message="Loading..." />;
  }

  return (
    <>
      <ListPageLayout
        breadcrumbs={[
          { label: "Dashboard", to: "/user" },
          { label: "Banking Account" },
        ]}
        title="Banking Account"
        subtitle="Manage payment accounts and banking details."
        showFilters={false}
        createLabel="Add"
        onCreateClick={() => setCreateOpen(true)}
      >
        <DataTable
          columns={COLUMNS}
          isEmpty={bankingAccounts.length === 0}
          emptyMessage="No banking accounts found"
        >
          <TableBody>
            {bankingAccounts.map((row, index) => (
              <DataTableRow key={row.id}>
                <DataTableCell>{page * rowsPerPage + index + 1}</DataTableCell>
                <DataTableCell>
                  <Avatar
                    alt="payment logo"
                    src={row.payment_logo_url}
                    sx={{ width: 56, height: 56 }}
                  >
                    B
                  </Avatar>
                </DataTableCell>
                <DataTableCell>{row?.payment_service_name}</DataTableCell>
                <DataTableCell>{row?.receiver_name}</DataTableCell>
                <DataTableCell>{row?.account_number}</DataTableCell>
                <DataTableCell>
                  {row?.information?.substring(0, 20)}...
                </DataTableCell>
                <DataTableCell>{row.created_at?.substring(0, 10)}</DataTableCell>
                <DataTableCell>
                  <RowActions
                    viewLabel="Detail"
                    onView={() => navigate(`/banking_acc/${row.id}`)}
                  />
                </DataTableCell>
              </DataTableRow>
            ))}
          </TableBody>
        </DataTable>

        <DataTablePagination
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          entityLabel="accounts"
        />
      </ListPageLayout>

      <Modal
        keepMounted
        open={createOpen}
        aria-labelledby="keep-mounted-modal-title"
        aria-describedby="keep-mounted-modal-description"
        sx={{
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box sx={{ maxWidth: 800, width: "100%", outline: "none" }}>
          <CreateBankingAcc
            handleClose={() => setCreateOpen(false)}
            packageId={packageId}
          />
        </Box>
      </Modal>
    </>
  );
};

export default BankingAccs;
